import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BOOKING_STATUS, PAYMENT_STATUS } from "@/lib/bookingConstants";
import { FieldSelect } from "@/components/ui/field-select";
import { IncidentDialog } from "@/components/fleet/FleetDialogs";
import ExpenseFormDrawer from "@/components/finance/ExpenseFormDrawer";
import { Loader2, Clock, Users as UsersIcon, Car, AlertTriangle, Receipt } from "lucide-react";

const QUICK = [["", "Toutes"], ["upcoming", "À venir"], ["in_progress", "En cours"], ["completed", "Terminées"], ["problems", "Problèmes"]];
const NEXT = { CONFIRMED: ["CHECKED_IN", "Check-in"], CHECKED_IN: ["IN_PROGRESS", "Démarrer"], IN_PROGRESS: ["COMPLETED", "Terminer"] };

export default function Operations() {
  const { orgId, can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [quick, setQuick] = useState("");
  const [incFor, setIncFor] = useState(null);
  const [expOpen, setExpOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["operations", orgId],
    queryFn: async () => (await api.get("/api/operations", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });
  const { data: fleetData } = useQuery({
    queryKey: ["fleet-min-ops", orgId],
    queryFn: async () => (await api.get("/api/fleet", { params: { organization_id: orgId } })).data,
    enabled: !!orgId && !!incFor,
  });
  const transition = useMutation({
    mutationFn: async ({ id, status }) => (await api.post(`/api/bookings/${id}/status`, { status }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Statut mis à jour"); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  const c = data?.counts || {};
  let rows = data?.bookings || [];
  if (quick === "upcoming") rows = rows.filter((b) => ["CONFIRMED", "CHECKED_IN"].includes(b.status));
  else if (quick === "in_progress") rows = rows.filter((b) => b.status === "IN_PROGRESS");
  else if (quick === "completed") rows = rows.filter((b) => b.status === "COMPLETED");
  else if (quick === "problems") rows = rows.filter((b) => b.needs_reassignment || b.guide_needs_reassignment || !b.assigned_employee_ids?.length || (b.status === "COMPLETED" && b.payment_status === "UNPAID"));

  return (
    <div className="mx-auto max-w-[1100px] space-y-5 p-4 sm:p-6 lg:p-8" data-testid="operations-page">
      <div><h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Opérations</h1><p className="text-sm text-slate-500">Journée du {data?.date || "—"}</p></div>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        <Stat label="À venir" value={c.upcoming} testid="ops-stat-upcoming" />
        <Stat label="En cours" value={c.in_progress} testid="ops-stat-inprogress" tone="text-amber-700" />
        <Stat label="Terminées" value={c.completed} testid="ops-stat-completed" tone="text-emerald-700" />
        <Stat label="Problèmes" value={c.problems} testid="ops-stat-problems" tone="text-rose-700" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap rounded-lg border border-slate-200 bg-white p-0.5">
          {QUICK.map(([k, l]) => <button key={k} onClick={() => setQuick(k)} data-testid={`ops-quick-${k || "all"}`} className={`rounded-md px-3 py-1.5 text-sm font-medium ${quick === k ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}
        </div>
        {can("expenses.manage") && <button onClick={() => setExpOpen(true)} data-testid="ops-new-expense" className="ml-auto flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><Receipt className="h-4 w-4" /> Dépense</button>}
      </div>

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="operations-empty"><Clock className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-3 text-sm text-slate-500">Aucune activité pour ce filtre.</p></div>
      ) : (
        <div className="space-y-3">
          {rows.map((b) => {
            const nx = NEXT[b.status];
            const problem = b.needs_reassignment || b.guide_needs_reassignment || !b.assigned_employee_ids?.length || (b.status === "COMPLETED" && b.payment_status === "UNPAID");
            return (
              <div key={b.id} data-testid={`ops-card-${b.id}`} className={`rounded-2xl border bg-white p-4 ${problem ? "border-rose-200" : "border-slate-200"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="text-lg font-bold text-slate-900">{b.start_time}</span><span className="truncate font-medium text-slate-700">{b.activity_name} · {b.participants_count} pax</span></div>
                    <p className="truncate text-sm text-slate-500">{b.customer_name} · <span className="font-mono text-xs">{b.booking_reference}</span></p>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><UsersIcon className="h-3.5 w-3.5" /> {b.guide_names?.length ? b.guide_names.join(", ") : <span className="text-rose-600">Sans guide</span>}</span>
                      {b.resource_refs?.length > 0 && <span className="flex items-center gap-1"><Car className="h-3.5 w-3.5" /> {b.resource_refs.join(", ")}</span>}
                      {b.needs_reassignment && <span className="rounded bg-rose-50 px-1.5 text-rose-600">Ressource à réaffecter</span>}
                      {b.guide_needs_reassignment && <span className="rounded bg-rose-50 px-1.5 text-rose-600">Guide à réaffecter</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${BOOKING_STATUS[b.status]?.cls}`}>{BOOKING_STATUS[b.status]?.label}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${PAYMENT_STATUS[b.payment_status]?.cls}`}>{PAYMENT_STATUS[b.payment_status]?.label}</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {nx && can("bookings.update_status") && <button onClick={() => transition.mutate({ id: b.id, status: nx[0] })} data-testid={`ops-advance-${b.id}`} className="flex-1 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 sm:flex-none">{nx[1]}</button>}
                  <button onClick={() => setIncFor(b)} data-testid={`ops-incident-${b.id}`} className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><AlertTriangle className="h-4 w-4 text-amber-500" /></button>
                  <button onClick={() => nav("/reservations")} data-testid={`ops-view-${b.id}`} className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Voir</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <IncidentDialog open={!!incFor} onOpenChange={() => setIncFor(null)}
        fixedResourceId={incFor?.assigned_vehicle_ids?.[0]} bookingId={incFor?.id}
        resources={fleetData?.resources || []} />
      <ExpenseFormDrawer open={expOpen} onOpenChange={setExpOpen} currency={data?.currency || "MAD"} />
    </div>
  );
}

const Stat = ({ label, value, testid, tone = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 text-center" data-testid={testid}><p className={`text-2xl font-bold ${tone}`}>{value ?? 0}</p><p className="text-xs text-slate-400">{label}</p></div>
);
